import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Film, ArrowLeft, Users, FolderKanban, CheckCircle2, Clock, 
  TrendingUp, BarChart3, Calendar, UserCheck, UserX, Search,
  MoreVertical, Eye, Edit, Trash2, Shield, Mail, Activity, Settings,
  MessageCircle, Phone, Image, Video, Plus, Bell, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [pendingTalents, setPendingTalents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  // New state for protection features
  const [auditLogs, setAuditLogs] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [platformSettings, setPlatformSettings] = useState(null);
  
  // WhatsApp intake state
  const [whatsappSessions, setWhatsappSessions] = useState([]);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchAdminData();
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/notifications?limit=20`, { headers, withCredentials: true });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/notifications/${notificationId}/read`, {}, { headers, withCredentials: true });
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/notifications/read-all`, {}, { headers, withCredentials: true });
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const fetchAdminData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, talentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/talents?status=pending`, { headers, withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setPendingTalents(talentsRes.data);

      if (isSuperAdmin) {
        const usersRes = await axios.get(`${API}/admin/users`, { headers, withCredentials: true });
        setAllUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTalentAction = async (talentId, action) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/talents/${talentId}/status?status=${action}`, {}, { headers, withCredentials: true });
      setPendingTalents(prev => prev.filter(t => t.talent_id !== talentId));
      
      // Update stats
      if (stats) {
        setStats(prev => ({
          ...prev,
          talents: {
            ...prev.talents,
            pending: prev.talents.pending - 1,
            approved: action === 'approved' ? prev.talents.approved + 1 : prev.talents.approved
          }
        }));
      }
    } catch (error) {
      console.error('Error updating talent:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { headers, withCredentials: true });
      setAllUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, role: newRole } : u
      ));
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [viewsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/admin/audit/profile-views?limit=50`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/audit/emails?limit=50`, { headers, withCredentials: true })
      ]);
      setAuditLogs(viewsRes.data.views);
      setEmailLogs(emailsRes.data.emails);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const fetchContactRequests = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/contact-requests`, { headers, withCredentials: true });
      setContactRequests(res.data);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
    }
  };

  const handleContactRequest = async (requestId, status) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/contact-requests/${requestId}?status=${status}`, {}, { headers, withCredentials: true });
      setContactRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (error) {
      console.error('Error handling request:', error);
    }
  };

  const handleVerifyUser = async (userId, verified) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/users/${userId}/verify?verified=${verified}`, {}, { headers, withCredentials: true });
      setAllUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, verified } : u
      ));
    } catch (error) {
      console.error('Error verifying user:', error);
    }
  };

  const fetchWhatsappSessions = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/whatsapp-sessions`, { headers, withCredentials: true });
      setWhatsappSessions(res.data);
    } catch (error) {
      console.error('Error fetching WhatsApp sessions:', error);
    }
  };

  const handleCreateTalentFromSession = async (sessionId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/whatsapp-sessions/${sessionId}/create-talent`, {}, { headers, withCredentials: true });
      // Refresh sessions
      fetchWhatsappSessions();
      alert('Talent profile created successfully!');
    } catch (error) {
      console.error('Error creating talent:', error);
      alert('Failed to create talent profile');
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'audit' && auditLogs.length === 0) {
      fetchAuditLogs();
    }
    if (activeTab === 'contacts' && contactRequests.length === 0) {
      fetchContactRequests();
    }
    if (activeTab === 'whatsapp' && whatsappSessions.length === 0) {
      fetchWhatsappSessions();
    }
  }, [activeTab]);

  const filteredUsers = allUsers.filter(u => {
    if (userRoleFilter && u.role !== userRoleFilter) return false;
    if (userSearch && !u.name?.toLowerCase().includes(userSearch.toLowerCase()) && 
        !u.email?.toLowerCase().includes(userSearch.toLowerCase())) return false;
    return true;
  });

  const defaultImages = [
    'https://images.unsplash.com/photo-1762160773119-adb4c011e4d7?w=200&q=80',
    'https://images.unsplash.com/photo-1762160767032-9a639bc9f89e?w=200&q=80'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 nav-glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Film className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl tracking-wide">CASTDECK</span>
            </Link>

            <div className="flex items-center gap-2">
              <span className="px-3 py-1 text-xs font-medium uppercase bg-primary/10 text-primary border border-primary/20">
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage talents, users, and platform analytics</p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="stat-card" data-testid="stat-total-talents">
              <Users className="w-6 h-6 text-primary mb-3" />
              <div className="text-3xl font-bold mb-1">{stats.talents?.total || 0}</div>
              <div className="text-sm text-muted-foreground">Total Talents</div>
            </div>
            <div className="stat-card" data-testid="stat-pending">
              <Clock className="w-6 h-6 text-yellow-400 mb-3" />
              <div className="text-3xl font-bold mb-1">{stats.talents?.pending || 0}</div>
              <div className="text-sm text-muted-foreground">Pending Approval</div>
            </div>
            <div className="stat-card" data-testid="stat-projects">
              <FolderKanban className="w-6 h-6 text-green-400 mb-3" />
              <div className="text-3xl font-bold mb-1">{stats.projects?.active || 0}</div>
              <div className="text-sm text-muted-foreground">Active Projects</div>
            </div>
            <div className="stat-card" data-testid="stat-bookings">
              <CheckCircle2 className="w-6 h-6 text-blue-400 mb-3" />
              <div className="text-3xl font-bold mb-1">{stats.bookings?.total || 0}</div>
              <div className="text-sm text-muted-foreground">Total Bookings</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'overview' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'pending' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-pending"
          >
            Pending Approvals
            {pendingTalents.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-yellow-500/10 text-yellow-400 rounded-full">
                {pendingTalents.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'contacts' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-contacts"
          >
            <Shield className="w-4 h-4" />
            Contact Requests
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'audit' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-audit"
          >
            <Activity className="w-4 h-4" />
            Audit Logs
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'whatsapp' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-whatsapp"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp Intake
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'users' 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-users"
            >
              User Management
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Talents */}
            <div className="bg-card border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Talents</h3>
              <div className="space-y-4">
                {stats?.recent_talents?.slice(0, 5).map((talent, index) => (
                  <div key={talent.talent_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img 
                        src={talent.photos?.[0] || defaultImages[index % 2]} 
                        alt={talent.full_name}
                        className="w-10 h-10 object-cover"
                      />
                      <div>
                        <div className="font-medium">{talent.full_name}</div>
                        <div className="text-sm text-muted-foreground">{talent.location}</div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs ${
                      talent.status === 'approved' ? 'bg-green-500/10 text-green-400' :
                      talent.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {talent.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Projects */}
            <div className="bg-card border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Projects</h3>
              <div className="space-y-4">
                {stats?.recent_projects?.slice(0, 5).map((project) => (
                  <div key={project.project_id} className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{project.name}</div>
                      <div className="text-sm text-muted-foreground capitalize">{project.project_type?.replace('_', ' ')}</div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {project.shortlisted_talents?.length || 0} talents
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pending' && (
          <div className="bg-card border border-border p-6">
            <h3 className="text-lg font-semibold mb-6">Talents Awaiting Approval</h3>
            {pendingTalents.length > 0 ? (
              <div className="space-y-4">
                {pendingTalents.map((talent, index) => (
                  <div 
                    key={talent.talent_id} 
                    className="flex items-center justify-between p-4 bg-muted/50 border border-border"
                    data-testid={`pending-talent-${talent.talent_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <img 
                        src={talent.photos?.[0] || defaultImages[index % 2]} 
                        alt={talent.full_name}
                        className="w-16 h-16 object-cover"
                      />
                      <div>
                        <h4 className="font-semibold">{talent.full_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {talent.age} yrs • {talent.gender} • {talent.location}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {talent.look_tags?.slice(0, 3).map(tag => (
                            <span key={tag} className="tag">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/talents/${talent.talent_id}`)}
                        className="border-white/10"
                        data-testid={`view-${talent.talent_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button 
                        onClick={() => handleTalentAction(talent.talent_id, 'approved')}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                        data-testid={`approve-${talent.talent_id}`}
                      >
                        <UserCheck className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        onClick={() => handleTalentAction(talent.talent_id, 'rejected')}
                        variant="destructive"
                        size="sm"
                        data-testid={`reject-${talent.talent_id}`}
                      >
                        <UserX className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state py-12">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-400 opacity-50" />
                <h4 className="text-lg font-semibold mb-2">All caught up!</h4>
                <p className="text-muted-foreground">No talents pending approval</p>
              </div>
            )}
          </div>
        )}

        {/* Contact Requests Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-card border border-border p-6" data-testid="contacts-section">
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Contact Access Requests
            </h3>
            {contactRequests.length > 0 ? (
              <div className="space-y-4">
                {contactRequests.map((request) => (
                  <div 
                    key={request.request_id} 
                    className="p-4 bg-muted/20 border border-border flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium">{request.requested_by_name}</div>
                      <div className="text-sm text-muted-foreground">
                        Requesting access to talent: {request.talent_id}
                      </div>
                      {request.reason && (
                        <div className="text-sm text-muted-foreground mt-1">
                          Reason: {request.reason}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(request.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        onClick={() => handleContactRequest(request.request_id, 'approved')}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <UserCheck className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        onClick={() => handleContactRequest(request.request_id, 'rejected')}
                        variant="destructive"
                        size="sm"
                      >
                        <UserX className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state py-12">
                <Shield className="w-12 h-12 mx-auto mb-4 text-primary opacity-50" />
                <h4 className="text-lg font-semibold mb-2">No pending requests</h4>
                <p className="text-muted-foreground">All contact access requests have been processed</p>
              </div>
            )}
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6" data-testid="audit-section">
            {/* Profile Views */}
            <div className="bg-card border border-border p-6">
              <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-400" />
                Profile View History
              </h3>
              {auditLogs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Viewer</th>
                        <th>Role</th>
                        <th>Talent ID</th>
                        <th>Time</th>
                        <th>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.view_id}>
                          <td>{log.viewed_by_name}</td>
                          <td>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              log.viewed_by_role === 'super_admin' ? 'bg-red-500/10 text-red-400' :
                              log.viewed_by_role === 'casting_manager' ? 'bg-purple-500/10 text-purple-400' :
                              log.viewed_by_role === 'client' ? 'bg-blue-500/10 text-blue-400' :
                              'bg-green-500/10 text-green-400'
                            }`}>
                              {log.viewed_by_role}
                            </span>
                          </td>
                          <td className="font-mono text-xs">{log.talent_id}</td>
                          <td>{new Date(log.viewed_at).toLocaleString()}</td>
                          <td className="font-mono text-xs">{log.ip_address || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state py-8">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-muted-foreground">No profile views recorded yet</p>
                </div>
              )}
            </div>

            {/* Email Logs */}
            <div className="bg-card border border-border p-6">
              <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-400" />
                Email Notification Log
              </h3>
              {emailLogs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>To</th>
                        <th>Subject</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Sent At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogs.map((log) => (
                        <tr key={log.email_id}>
                          <td>{log.to_email}</td>
                          <td className="max-w-xs truncate">{log.subject}</td>
                          <td>
                            <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                              {log.template_type}
                            </span>
                          </td>
                          <td>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              log.status === 'sent' ? 'bg-green-500/10 text-green-400' :
                              log.status === 'mocked' ? 'bg-yellow-500/10 text-yellow-400' :
                              'bg-red-500/10 text-red-400'
                            }`}>
                              {log.status}
                            </span>
                          </td>
                          <td>{new Date(log.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state py-8">
                  <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-muted-foreground">No emails sent yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* WhatsApp Intake Tab */}
        {activeTab === 'whatsapp' && (
          <div className="space-y-6" data-testid="whatsapp-section">
            {/* Setup Instructions */}
            <div className="bg-card border border-border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-400" />
                WhatsApp Talent Intake Setup
              </h3>
              <div className="bg-muted/20 p-4 rounded-sm mb-4">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Webhook URL:</strong> Configure this URL in your WABA Connect dashboard:
                </p>
                <code className="block bg-black/50 p-3 text-sm text-green-400 rounded">
                  {BACKEND_URL}/api/whatsapp/webhook
                </code>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>Verify Token:</strong> <code className="bg-black/50 px-2 py-1 text-xs">castdeck_whatsapp_2024</code>
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-2">How it works:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Casting managers send talent info + photos to your WhatsApp Business number</li>
                  <li>System automatically extracts: Name, age, height, gender, location, experience</li>
                  <li>Photos and videos are uploaded to cloud storage</li>
                  <li>Talent profile is created (pending approval)</li>
                  <li>Send "done" to finalize the profile</li>
                </ol>
              </div>
            </div>

            {/* Active Sessions */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-400" />
                  Intake Sessions
                </h3>
                <Button onClick={fetchWhatsappSessions} variant="outline" className="border-white/10" size="sm">
                  Refresh
                </Button>
              </div>
              
              {whatsappSessions.length > 0 ? (
                <div className="space-y-4">
                  {whatsappSessions.map((session) => (
                    <div 
                      key={session.session_id}
                      className="p-4 bg-muted/20 border border-border"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-sm">{session.phone}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              session.status === 'active' ? 'bg-green-500/10 text-green-400' :
                              session.status === 'completed' ? 'bg-blue-500/10 text-blue-400' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {session.status}
                            </span>
                          </div>
                          
                          {/* Parsed Data */}
                          {session.parsed_data && (
                            <div className="text-sm text-muted-foreground mb-2">
                              <span className="font-medium text-foreground">
                                {session.parsed_data.full_name || 'Name pending...'}
                              </span>
                              {session.parsed_data.age && <span> • {session.parsed_data.age} yrs</span>}
                              {session.parsed_data.gender && <span> • {session.parsed_data.gender}</span>}
                              {session.parsed_data.location && <span> • {session.parsed_data.location}</span>}
                            </div>
                          )}
                          
                          {/* Media Count */}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Image className="w-3 h-3" />
                              {session.photos?.length || 0} photos
                            </span>
                            <span className="flex items-center gap-1">
                              <Video className="w-3 h-3" />
                              {session.videos?.length || 0} videos
                            </span>
                            <span>{new Date(session.created_at).toLocaleString()}</span>
                          </div>
                          
                          {/* Photos Preview */}
                          {session.photos?.length > 0 && (
                            <div className="flex gap-2 mt-3">
                              {session.photos.slice(0, 4).map((photo, idx) => (
                                <img 
                                  key={idx}
                                  src={photo} 
                                  alt={`Photo ${idx + 1}`}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              ))}
                              {session.photos.length > 4 && (
                                <div className="w-12 h-12 bg-muted flex items-center justify-center rounded text-xs">
                                  +{session.photos.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {session.status === 'active' && (
                            <Button
                              onClick={() => handleCreateTalentFromSession(session.session_id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Plus className="w-4 h-4 mr-1" /> Create Profile
                            </Button>
                          )}
                          {session.talent_id && (
                            <Button
                              onClick={() => navigate(`/talents/${session.talent_id}`)}
                              size="sm"
                              variant="outline"
                              className="border-white/10"
                            >
                              <Eye className="w-4 h-4 mr-1" /> View Talent
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state py-12">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-green-400 opacity-50" />
                  <h4 className="text-lg font-semibold mb-2">No intake sessions yet</h4>
                  <p className="text-muted-foreground">
                    Send talent details to your WhatsApp Business number to start
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && isSuperAdmin && (
          <div className="bg-card border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">User Management</h3>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users..."
                    className="pl-10 w-64 bg-input/20 border-white/10"
                    data-testid="user-search"
                  />
                </div>
                <Select value={userRoleFilter || "all"} onValueChange={(v) => setUserRoleFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48 bg-input/20 border-white/10" data-testid="role-filter">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="casting_manager">Casting Manager</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="talent">Talent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">
                              {u.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className="text-muted-foreground">{u.email}</td>
                      <td>
                        <Select 
                          value={u.role} 
                          onValueChange={(v) => handleRoleChange(u.user_id, v)}
                        >
                          <SelectTrigger className="w-40 h-8 bg-input/20 border-white/10 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                            <SelectItem value="casting_manager">Casting Manager</SelectItem>
                            <SelectItem value="client">Client</SelectItem>
                            <SelectItem value="talent">Talent</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        {u.verified ? (
                          <span className="px-2 py-1 text-xs bg-green-500/10 text-green-400 rounded-full flex items-center gap-1 w-fit">
                            <CheckCircle2 className="w-3 h-3" /> Verified
                          </span>
                        ) : (
                          <Button
                            onClick={() => handleVerifyUser(u.user_id, true)}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                          >
                            <Shield className="w-3 h-3 mr-1" /> Verify
                          </Button>
                        )}
                      </td>
                      <td className="text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-white/5 rounded">
                              <MoreVertical className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {u.verified && (
                              <DropdownMenuItem onClick={() => handleVerifyUser(u.user_id, false)}>
                                <Shield className="w-4 h-4 mr-2" /> Revoke Verification
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
